import { useState, useEffect } from "react";
import { ACCESS_TOKEN } from "@/constants";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

function AdminModal({ open, onClose, admin, onSuccess }) {
  function evaluatePassword(pw) {
    const lengthOK = pw.length >= 6;
    const hasLower = /[a-z]/.test(pw);
    const hasUpper = /[A-Z]/.test(pw);
    const hasNumber = /\d/.test(pw);
    const hasSpecial = /[^A-Za-z0-9]/.test(pw);

    const score =
      (lengthOK ? 1 : 0) +
      (hasLower ? 1 : 0) +
      (hasUpper ? 1 : 0) +
      (hasNumber ? 1 : 0) +
      (hasSpecial ? 1 : 0);

    let label = "";
    let color = "";

    if (!pw) {
      label = "";
      color = "";
    } else if (score <= 2) {
      label = "Weak";
      color = "bg-red-500";
    } else if (score === 3) {
      label = "Moderate";
      color = "bg-yellow-500";
    } else {
      label = "Strong";
      color = "bg-green-500";
    }

    return { score, label, color };
  }

  const isEdit = Boolean(admin);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const [formData, setFormData] = useState({
    name: "",
    username: "",
    email: "",
    phone: "",
    password: "",
    role: "",
    status: "",
  });

  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    label: "",
    color: "",
  });

  const [passwordError, setPasswordError] = useState("");

  useEffect(() => {
    if (isEdit && admin) {
      const rawPhone = admin.phone || "";

      setFormData({
        name: admin.name || "",
        username: admin.username || "",
        email: admin.email || "",
        phone: rawPhone.replace(/\s+/g, ""),
        password: "",
        role: admin.role || "",
        status: admin.status || "",
      });

      setPasswordStrength({
        score: 0,
        label: "",
        color: "",
      });
      setPasswordError("");
    } else {
      // reset when adding a new user
      setFormData({
        name: "",
        username: "",
        email: "",
        phone: "",
        password: "",
        role: "",
        status: "",
      });

      setPasswordStrength({
        score: 0,
        label: "",
        color: "",
      });
      setPasswordError("");
    }
  }, [admin, isEdit]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;

    setFormData((prev) => ({ ...prev, password: value }));
    setPasswordError("");

    const strength = evaluatePassword(value);
    setPasswordStrength(strength);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    setPasswordError("");
    setLoading(true);

    const token =
      localStorage.getItem(ACCESS_TOKEN) ||
      sessionStorage.getItem(ACCESS_TOKEN);

    if (!token) {
      setLoading(false);
      setErrorMsg("Session expired. Please log in again.");
      return;
    }

    // password rules only when adding a new user
    if (!isEdit) {
      const pw = formData.password || "";
      const strength = evaluatePassword(pw);

      setPasswordStrength(strength);

      if (pw.length < 6) {
        setLoading(false);
        setPasswordError("Password must be at least 6 characters.");
        return;
      }

      if (strength.score < 3) {
        setLoading(false);
        setPasswordError(
          "Password is too weak. Use upper & lower case letters and at least one number or symbol.",
        );
        return;
      }
    }

    const API_URL = import.meta.env.VITE_API_URL;

    const url = isEdit
      ? `${API_URL}/api/admin/update_user/${admin.id}`
      : `${API_URL}/api/admin/add_user`;

    const method = isEdit ? "PUT" : "POST";

    try {
      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        credentials: "include",
        body: JSON.stringify({
          name: formData.name,
          username: formData.username,
          email: formData.email,
          phone: formData.phone,
          password: formData.password,
          role: formData.role,
          status: formData.status,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.message ||
            (isEdit ? "Failed to update admin" : "Failed to add admin"),
        );
      }

      console.log(isEdit ? "Admin updated:" : "Admin added:", data);
      onSuccess();
      onClose();
    } catch (error) {
      console.error("Error:", error);
      setErrorMsg(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      modal={false}
      onOpenChange={(isOpen) => {
        if (!isOpen) onClose();
      }}>
      <DialogContent className="w-[95vw] sm:max-w-[600px] max-h-[85vh] overflow-y-auto z-[1000]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{isEdit ? "Edit User" : "Add New User"}</DialogTitle>
            <DialogDescription>
              {isEdit
                ? "Update the user details below."
                : "Fill in the details to add a new user."}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4 px-1 sm:px-0">
            {/* Name */}
            <div className="grid grid-cols-1 sm:grid-cols-4 items-start sm:items-center gap-2 sm:gap-4">
              <Label htmlFor="name" className="sm:pt-0">
                Name
              </Label>
              <Input
                id="name"
                name="name"
                placeholder="e.g., Juan Dela Cruz"
                className="w-full sm:col-span-3"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </div>

            {/* Username */}
            <div className="grid grid-cols-1 sm:grid-cols-4 items-start sm:items-center gap-2 sm:gap-4">
              <Label htmlFor="username">Username</Label>
              <Input
                id="username"
                name="username"
                placeholder="e.g., juandelacruz"
                className="w-full sm:col-span-3"
                value={formData.username}
                onChange={handleChange}
                required
              />
            </div>

            {/* Email */}
            <div className="grid grid-cols-1 sm:grid-cols-4 items-start sm:items-center gap-2 sm:gap-4">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="e.g., juan@gmail.com"
                className="w-full sm:col-span-3"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>

            {/* Phone */}
            <div className="grid grid-cols-1 sm:grid-cols-4 items-start sm:items-center gap-2 sm:gap-4">
              <Label htmlFor="phone">Phone</Label>

              <div className="w-full sm:col-span-3">
                <PhoneInput
                  country={"au"}
                  value={formData.phone}
                  onChange={(value) =>
                    setFormData((prev) => ({
                      ...prev,
                      phone: value.startsWith("+") ? value : "+" + value,
                    }))
                  }
                  enableSearch={true}
                  countryCodeEditable={false}
                  inputProps={{
                    name: "phone",
                    required: true,
                  }}
                  containerClass="w-full"
                  inputClass="!w-full !h-10 !text-sm !border !border-input !bg-background !rounded-md !px-3 !py-2"
                  buttonClass="!h-10 !border !border-input !rounded-md !bg-background"
                />
              </div>
            </div>

            {/* Password (only on add) */}
            {!isEdit && (
              <div className="grid grid-cols-1 sm:grid-cols-4 items-start gap-2 sm:gap-4">
                <Label htmlFor="password" className="sm:pt-2">
                  Password
                </Label>

                <div className="w-full sm:col-span-3">
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    placeholder="Minimum 6 characters"
                    value={formData.password}
                    onChange={handlePasswordChange}
                    required
                  />

                  {passwordStrength.label && (
                    <div className="mt-2">
                      <div className="h-1.5 w-full bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className={`h-full ${passwordStrength.color} transition-all`}
                          style={{
                            width: `${(passwordStrength.score / 4) * 100}%`,
                          }}
                        />
                      </div>

                      <p className="mt-1 text-xs text-gray-600">
                        Password strength:{" "}
                        <span className="font-medium">
                          {passwordStrength.label}
                        </span>
                      </p>
                    </div>
                  )}

                  <p className="mt-2 text-xs text-gray-500">
                    Must be at least 6 characters and include upper &amp; lower
                    case letters and at least one number or special symbol.
                  </p>

                  {passwordError && (
                    <p className="mt-1 text-xs text-red-500">{passwordError}</p>
                  )}
                </div>
              </div>
            )}

            {/* Role */}
            <div className="grid grid-cols-1 sm:grid-cols-4 items-start sm:items-center gap-2 sm:gap-4">
              <Label htmlFor="role">Role</Label>
              <Select
                value={formData.role}
                onValueChange={(value) =>
                  setFormData((prev) => ({ ...prev, role: value }))
                }>
                <SelectTrigger className="w-full sm:col-span-3">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent className="z-[9999]">
                  <SelectItem value="Owner">Owner</SelectItem>
                  <SelectItem value="Admin">Admin</SelectItem>
                  <SelectItem value="Manager">Manager</SelectItem>
                  <SelectItem value="SDA Owner">SDA Owner</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Status */}
            <div className="grid grid-cols-1 sm:grid-cols-4 items-start sm:items-center gap-2 sm:gap-4">
              <Label htmlFor="status">Status</Label>
              <Select
                value={formData.status}
                onValueChange={(value) =>
                  setFormData((prev) => ({ ...prev, status: value }))
                }>
                <SelectTrigger className="w-full sm:col-span-3">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent className="z-[9999]">
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="w-full sm:w-auto">
              Cancel
            </Button>

            <Button
              type="submit"
              className="w-full sm:w-auto bg-[#D2138C] hover:bg-[#B01076] text-white"
              disabled={loading}>
              {loading
                ? isEdit
                  ? "Saving..."
                  : "Adding..."
                : isEdit
                  ? "Save Changes"
                  : "Add User"}
            </Button>
          </DialogFooter>

          {errorMsg && (
            <p className="text-red-500 text-sm mt-2 text-center">{errorMsg}</p>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default AdminModal;
